import { ApiError } from '@/lib/api/errors.js';
import { getExportDownloadUrl } from '@/lib/api/reports-client.js';
import { useCallback } from 'react';

export interface DownloadError {
  kind: 'expired' | 'not_ready' | 'unknown';
  message: string;
  requestId?: string | undefined;
}

export interface UseDownloadExportResult {
  download: (exportId: string) => Promise<{ error: DownloadError | null }>;
}

/**
 * Returns a stable `download` callback that:
 * 1. Calls the backend with Accept: application/json to get the signed URL
 *    while preserving the Authorization header (B-1 fix — browser tab navigation
 *    would drop the header and receive 401).
 * 2. Programmatically clicks a temporary <a> to trigger the file download.
 * 3. Returns a typed error discriminant so callers can show the right message.
 */
export function useDownloadExport(): UseDownloadExportResult {
  const download = useCallback(
    async (exportId: string): Promise<{ error: DownloadError | null }> => {
      try {
        const { downloadUrl } = await getExportDownloadUrl(exportId);

        const a = document.createElement('a');
        a.href = downloadUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        return { error: null };
      } catch (err) {
        if (err instanceof ApiError) {
          // W-5: map well-known export error codes to actionable messages
          if (err.code === 'EXPORT_EXPIRED') {
            return {
              error: {
                kind: 'expired',
                message: 'Esta exportación ha expirado. Solicita una nueva.',
                requestId: err.requestId,
              },
            };
          }
          if (err.code === 'EXPORT_NOT_READY') {
            return {
              error: {
                kind: 'not_ready',
                message: 'La exportación aún no está lista. Espera unos segundos.',
                requestId: err.requestId,
              },
            };
          }
          return {
            error: {
              kind: 'unknown',
              message: `Error al descargar (${err.code}${err.requestId !== undefined ? ` · ${err.requestId}` : ''}).`,
              requestId: err.requestId,
            },
          };
        }
        return {
          error: {
            kind: 'unknown',
            message: 'Error inesperado al descargar la exportación.',
          },
        };
      }
    },
    [],
  );

  return { download };
}
